import * as React from 'react';
import { useQuery } from 'react-query';

import { useFetchPaginatedTemperatureControl } from 'hooks/api/temperature-control';
import {
  useTemperatureControlSearchParams,
} from 'hooks/store/temperature-control/use-temperature-control-search-params';
import { Scaffold } from 'layouts';
import { PageHeaderForFilters } from 'components/ui/page-header-for-filters';
import { DataTableV2, type SortingProps } from 'components/ui/DataTableV2';

import { useTemperatureControlColumns } from './hooks';
import {
  TemperatureControlFilter,
  type TemperatureControlFiltersRef,
  TemperatureControleReport,
} from './components';
import { TabbedNavigation } from '../shared-components';

export const TemperatureControlListPage: React.FC = () => {
  const filtersRef = React.useRef<TemperatureControlFiltersRef>(null);
  const { fetchPaginatedTemperatureControl } =
    useFetchPaginatedTemperatureControl();
  const {
    filters,
    paginationControl,
    updateFilters,
    updatePaginationControl,
    clearState,
  } = useTemperatureControlSearchParams({ persisted: true });

  const columns = useTemperatureControlColumns();

  const temperatureControlQuery = useQuery({
    queryKey: [
      'fetch-paginated-temperature-control',
      filters,
      paginationControl,
    ],
    queryFn: () => {
      return fetchPaginatedTemperatureControl({
        filters,
        pagination: paginationControl,
      });
    },
    refetchOnWindowFocus: false,
  });

  const handleSort = React.useCallback(
    (p: SortingProps) => {
      updatePaginationControl({ ...p, page: 1 });
    },
    [updatePaginationControl],
  );

  const handleFilters = React.useCallback(
    (f: typeof filters) => {
      updateFilters(f);
      updatePaginationControl({ page: 1 });
    },
    [updateFilters, updatePaginationControl],
  );

  return (
    <Scaffold>
      <TabbedNavigation />
      <PageHeaderForFilters
        headerTitle={<span className={'font-GilroySemibold'}>Filtros</span>}
      >
        <TemperatureControlFilter
          ref={filtersRef}
          onSubmitFilters={handleFilters}
          onResetFilters={clearState}
        />
      </PageHeaderForFilters>
      <section className="mt-4">
        <DataTableV2
          data={temperatureControlQuery?.data?.data ?? []}
          columns={columns}
          manualPagination
          onPaginationChange={updatePaginationControl}
          onSort={handleSort}
          isLoading={temperatureControlQuery.isLoading}
          renderTopBar={() => (
            <div className="flex w-full justify-end">
              <TemperatureControleReport />
            </div>
          )}
          pagination={{
            ...temperatureControlQuery?.data?.meta,
            currentPage: paginationControl.page,
            perPage: paginationControl.limit,
          }}
        />
      </section>
    </Scaffold>
  );
};
